import {
  createContext,
  startTransition,
  useContext,
  useEffect,
  useState,
} from 'react';
import { createOfflineDataCache } from '@tov-plus/pwa';
import {
  fetchTovQuery,
  invalidateTovQuery,
  useTovQueryClient,
} from '@tov-plus/frontend-data';

import { guestApi } from '../app/api';
import {
  classifyGuestAccessFailure,
  getGuestResumeStorageKey,
  PUBLIC_GUEST_ACCESS_SENTINEL,
  resolveGuestAccessToken,
  resolveGuestEntryIntent,
  resolveGuestEntryRedirect,
  resolvePlannerGuestViewAs,
  resolveReviewPass,
  resolveResumeCode,
  resolveOccasionSlug,
  resolveGuestResumePath,
  isDistributionToken,
  removeTokenFromUrl,
  shouldRenderGuestAccessOffline,
} from '../app/access';
import { guestEnv } from '../config/env';
import {
  buildGuestExperience,
  type GuestExperience,
} from '../lib/experience/guestExperience';
import {
  parseRecentGuestAccessEntries,
  recentGuestAccessStorageKey,
} from '../lib/storage/recentGuestAccess';
import { guestQueryKeys } from '../lib/data/guestQueryKeys';
import { msg } from '@tov-plus/ui';

const guestTokenStorageKey = 'tov-plus.guests.guestToken';
const guestReadOnlyStorageKey = 'tov-plus.guests.readOnly';
const guestBootstrapStaleTimeMs = 15_000;

type GuestAccessState =
  | {
      status: 'loading';
      experience: null;
      occasionSlug: string | null;
      guestToken: string | null;
      errorMessage: null;
    }
  | {
      status:
        | 'missing_slug'
        | 'missing_token'
        | 'invalid_token'
        | 'occasion_unavailable'
        | 'offline'
        | 'error';
      experience: null;
      occasionSlug: string | null;
      guestToken: string | null;
      errorMessage: string;
    }
  | {
      status: 'ready';
      experience: GuestExperience;
      occasionSlug: string;
      guestToken: string;
      viewerMode:
        | 'guest'
        | 'public_visitor'
        | 'planner_public_preview'
        | 'planner_guest_preview';
      isReadOnly: boolean;
      entryMode: 'standard' | 'invitation_rsvp';
      entryIntent: ReturnType<typeof resolveGuestEntryIntent>;
      entryRedirect: ReturnType<typeof resolveGuestEntryRedirect>;
      resumePath: string | null;
      errorMessage: null;
    };

type GuestAccessContextValue = GuestAccessState & {
  refresh(): void;
  clearStoredToken(): void;
  consumeEntryRedirect(): void;
  reviewPass: ReturnType<typeof resolveReviewPass>;
};

const GuestAccessContext = createContext<GuestAccessContextValue | null>(null);

function readStoredGuestToken(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.localStorage.getItem(guestTokenStorageKey);
}

function persistGuestToken(token: string | null) {
  if (typeof window === 'undefined') {
    return;
  }

  if (token) {
    window.localStorage.setItem(guestTokenStorageKey, token);
  } else {
    window.localStorage.removeItem(guestTokenStorageKey);
  }
}

function getGuestAccessOfflineMessage(error: unknown) {
  return classifyGuestAccessFailure(error).message;
}

function persistRecentGuestAccess(input: {
  occasionSlug: string;
  occasionDisplayName: string;
  guestDisplayName: string;
  resumePath: string | null;
  localeCode: string;
  timezone: string | null;
}) {
  if (typeof window === 'undefined') {
    return;
  }

  const entries = parseRecentGuestAccessEntries(
    window.localStorage.getItem(recentGuestAccessStorageKey),
  );
  const next = [
    {
      occasionSlug: input.occasionSlug,
      occasionDisplayName: input.occasionDisplayName,
      guestDisplayName: input.guestDisplayName,
      resumePath: input.resumePath,
      localeCode: input.localeCode,
      timezone: input.timezone,
      seenAt: new Date().toISOString(),
    },
    ...entries.filter(
      (entry) =>
        !entry ||
        typeof entry !== 'object' ||
        (entry as { occasionSlug?: string }).occasionSlug !==
          input.occasionSlug,
    ),
  ].slice(0, 6);

  window.localStorage.setItem(
    recentGuestAccessStorageKey,
    JSON.stringify(next),
  );
}

function readStoredResumePath(occasionSlug: string, guestToken: string) {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.localStorage.getItem(
    getGuestResumeStorageKey(occasionSlug, guestToken),
  );
}

export function GuestAccessProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const queryClient = useTovQueryClient();
  const [state, setState] = useState<GuestAccessState>({
    status: 'loading',
    experience: null,
    occasionSlug: null,
    guestToken: null,
    errorMessage: null,
  });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let disposed = false;

    async function resolveAccessFromQueryCache() {
      startTransition(() => {
        setState({
          status: 'loading',
          experience: null,
          occasionSlug: null,
          guestToken: null,
          errorMessage: null,
        });
      });

      const occasionSlug = resolveOccasionSlug({
        hostname: window.location.hostname,
        pathname: window.location.pathname,
        search: window.location.search,
        envOccasionSlug: guestEnv.defaultOccasionSlug,
      });
      const reviewPass = resolveReviewPass(window.location.search);
      const resumeCode = resolveResumeCode(window.location.search);
      const plannerViewAs = resolvePlannerGuestViewAs(window.location.search);
      const entryIntent = resolveGuestEntryIntent(window.location.search);
      const entryMode =
        entryIntent.source === 'invite' &&
        entryIntent.requestedTarget?.trim().toLowerCase() === 'rsvp'
          ? 'invitation_rsvp'
          : 'standard';
      const guestToken = resolveGuestAccessToken({
        search: window.location.search,
        storedToken: readStoredGuestToken(),
        envToken: guestEnv.defaultGuestToken,
      });

      if (!occasionSlug) {
        startTransition(() => {
          setState({
            status: 'missing_slug',
            experience: null,
            occasionSlug: null,
            guestToken,
            errorMessage:
              'The guest shell could not resolve an occasion slug from the path, query params, hostname, or local dev config.',
          });
        });
        return;
      }

      if (!guestToken && resumeCode) {
        try {
          const exchangeResult = await guestApi.exchangeSecureResumeCode(
            occasionSlug,
            resumeCode,
          );
          persistGuestToken(exchangeResult.sessionToken);
          removeTokenFromUrl();
          window.localStorage.setItem(
            getGuestResumeStorageKey(occasionSlug, exchangeResult.sessionToken),
            exchangeResult.resumePath ?? '/',
          );
          window.location.reload();
          return;
        } catch (error) {
          if (shouldRenderGuestAccessOffline(error)) {
            startTransition(() => {
              setState({
                status: 'offline',
                experience: null,
                occasionSlug,
                guestToken: null,
                errorMessage: getGuestAccessOfflineMessage(error),
              });
            });
            return;
          }

          startTransition(() => {
            setState({
              status: 'invalid_token',
              experience: null,
              occasionSlug,
              guestToken: null,
              errorMessage:
                'This secure resume code is invalid, expired, or already used. Request a fresh resume packet from a trusted device.',
            });
          });
          return;
        }
      }

      if (!guestToken && !plannerViewAs) {
        const publicToken = PUBLIC_GUEST_ACCESS_SENTINEL;

        try {
          const bootstrap = await fetchTovQuery(queryClient, {
            queryKey: guestQueryKeys.bootstrap(occasionSlug, publicToken),
            queryFn: () =>
              guestApi.getExperienceBootstrap(occasionSlug, publicToken),
            staleTime: guestBootstrapStaleTimeMs,
          });
          const experience = await buildGuestExperience(
            bootstrap.context,
            bootstrap.openingExperience,
            bootstrap.events,
            bootstrap.forms,
            bootstrap.journeySummary,
            bootstrap.guestSession,
            bootstrap.onboardingSummary,
            bootstrap.accessLifecycle,
            bootstrap.guestStateMachine,
            bootstrap.guestIntelligence,
            bootstrap.guestIntelligenceSnapshots,
            bootstrap.tasks,
            bootstrap.recentCommunications,
            bootstrap.designTheme,
            bootstrap.eventVisualOverrides,
            bootstrap.tokenInheritance,
            bootstrap.guestWebsite,
          );

          if (disposed) {
            return;
          }

          window.sessionStorage.setItem(guestReadOnlyStorageKey, '1');
          startTransition(() => {
            setState({
              status: 'ready',
              experience,
              occasionSlug,
              guestToken: publicToken,
              viewerMode: 'public_visitor',
              isReadOnly: true,
              entryMode: 'standard',
              entryIntent,
              entryRedirect: {
                intent: entryIntent,
                target: 'home',
                path: '/',
                didFallback: false,
                reason: 'recommended',
              },
              resumePath: null,
              errorMessage: null,
            });
          });
          return;
        } catch (error) {
          if (shouldRenderGuestAccessOffline(error)) {
            startTransition(() => {
              setState({
                status: 'offline',
                experience: null,
                occasionSlug,
                guestToken: null,
                errorMessage: getGuestAccessOfflineMessage(error),
              });
            });
            return;
          }

          startTransition(() => {
            setState({
              status: 'missing_token',
              experience: null,
              occasionSlug,
              guestToken: null,
              errorMessage: reviewPass
                ? reviewPass.isValid
                  ? `Reviewer pass validated for ${reviewPass.previewMode.replaceAll('_', ' ')} until ${reviewPass.expiresAt}. Open the planner-issued guest preview link that includes invite context if you need the full guest shell.`
                  : msg(
                      'contexts.guest_access_context.this_reviewer_pass_is_invalid_or_expired_request_a_fresh_planner_review',
                    )
                : msg(
                    'contexts.guest_access_context.guest_access_is_token_first_open_the_experience_from_an_invite_link',
                  ),
            });
          });
          return;
        }
      }

      if (plannerViewAs) {
        const previewToken = guestToken ?? PUBLIC_GUEST_ACCESS_SENTINEL;

        try {
          const bootstrap = await fetchTovQuery(queryClient, {
            queryKey: guestQueryKeys.bootstrap(occasionSlug, previewToken),
            queryFn: () =>
              guestApi.getExperienceBootstrap(occasionSlug, previewToken),
            staleTime: guestBootstrapStaleTimeMs,
          });
          const experience = await buildGuestExperience(
            bootstrap.context,
            bootstrap.openingExperience,
            bootstrap.events,
            bootstrap.forms,
            bootstrap.journeySummary,
            bootstrap.guestSession,
            bootstrap.onboardingSummary,
            bootstrap.accessLifecycle,
            bootstrap.guestStateMachine,
            bootstrap.guestIntelligence,
            bootstrap.guestIntelligenceSnapshots,
            bootstrap.tasks,
            bootstrap.recentCommunications,
            bootstrap.designTheme,
            bootstrap.eventVisualOverrides,
            bootstrap.tokenInheritance,
            bootstrap.guestWebsite,
          );

          if (disposed) {
            return;
          }

          window.sessionStorage.setItem(guestReadOnlyStorageKey, '1');
          startTransition(() => {
            setState({
              status: 'ready',
              experience,
              occasionSlug,
              guestToken: previewToken,
              viewerMode:
                plannerViewAs.mode === 'guest'
                  ? 'planner_guest_preview'
                  : 'planner_public_preview',
              isReadOnly: true,
              entryMode: 'standard',
              entryIntent,
              entryRedirect: {
                intent: entryIntent,
                target: 'home',
                path: '/',
                didFallback: false,
                reason: 'recommended',
              },
              resumePath: null,
              errorMessage: null,
            });
          });
          return;
        } catch (error) {
          if (disposed) {
            return;
          }

          if (shouldRenderGuestAccessOffline(error)) {
            startTransition(() => {
              setState({
                status: 'offline',
                experience: null,
                occasionSlug,
                guestToken: previewToken,
                errorMessage: getGuestAccessOfflineMessage(error),
              });
            });
            return;
          }

          const failure = classifyGuestAccessFailure(error);

          startTransition(() => {
            setState({
              status:
                failure.kind === 'invalid_token'
                  ? 'invalid_token'
                  : failure.kind === 'occasion_unavailable'
                    ? 'occasion_unavailable'
                    : 'error',
              experience: null,
              occasionSlug,
              guestToken: previewToken,
              errorMessage: failure.message,
            });
          });
          return;
        }
      }

      if (!guestToken) {
        startTransition(() => {
          setState({
            status: 'missing_token',
            experience: null,
            occasionSlug,
            guestToken: null,
            errorMessage: reviewPass
              ? reviewPass.isValid
                ? `Reviewer pass validated for ${reviewPass.previewMode.replaceAll('_', ' ')} until ${reviewPass.expiresAt}. Open the planner-issued guest preview link that includes invite context if you need the full guest shell.`
                : msg(
                    'contexts.guest_access_context.this_reviewer_pass_is_invalid_or_expired_request_a_fresh_planner_review',
                  )
              : msg(
                  'contexts.guest_access_context.guest_access_is_token_first_open_the_experience_from_an_invite_link',
                ),
          });
        });
        return;
      }

      let activeToken = guestToken;

      if (isDistributionToken(guestToken, window.location.search)) {
        try {
          const exchangeResult = await guestApi.exchangeToken(guestToken);
          activeToken = exchangeResult.sessionToken;
          removeTokenFromUrl();
        } catch (error) {
          if (shouldRenderGuestAccessOffline(error)) {
            startTransition(() => {
              setState({
                status: 'offline',
                experience: null,
                occasionSlug,
                guestToken,
                errorMessage: getGuestAccessOfflineMessage(error),
              });
            });
            return;
          }

          const failure = classifyGuestAccessFailure(error);

          startTransition(() => {
            setState({
              status:
                failure.kind === 'invalid_token'
                  ? 'invalid_token'
                  : failure.kind === 'occasion_unavailable'
                    ? 'occasion_unavailable'
                    : 'error',
              experience: null,
              occasionSlug,
              guestToken,
              errorMessage: failure.message,
            });
          });
          return;
        }
      }

      try {
        const bootstrap = await fetchTovQuery(queryClient, {
          queryKey: guestQueryKeys.bootstrap(occasionSlug, activeToken),
          queryFn: () =>
            guestApi.getExperienceBootstrap(occasionSlug, activeToken),
          staleTime: guestBootstrapStaleTimeMs,
        });
        const experience = await buildGuestExperience(
          bootstrap.context,
          bootstrap.openingExperience,
          bootstrap.events,
          bootstrap.forms,
          bootstrap.journeySummary,
          bootstrap.guestSession,
          bootstrap.onboardingSummary,
          bootstrap.accessLifecycle,
          bootstrap.guestStateMachine,
          bootstrap.guestIntelligence,
          bootstrap.guestIntelligenceSnapshots,
          bootstrap.tasks,
          bootstrap.recentCommunications,
          bootstrap.designTheme,
          bootstrap.eventVisualOverrides,
          bootstrap.tokenInheritance,
          bootstrap.guestWebsite,
        );
        const entryRedirect = resolveGuestEntryRedirect({
          search: window.location.search,
          summary: bootstrap.journeySummary,
        });
        const resumePath = resolveGuestResumePath({
          storedPath: readStoredResumePath(occasionSlug, activeToken),
          summary: bootstrap.journeySummary,
        });

        if (disposed) {
          return;
        }

        persistGuestToken(activeToken);
        window.sessionStorage.setItem(guestReadOnlyStorageKey, '0');
        persistRecentGuestAccess({
          occasionSlug,
          occasionDisplayName: bootstrap.context.occasion.displayName,
          guestDisplayName: bootstrap.context.guestDisplayName,
          resumePath,
          localeCode: bootstrap.context.localizationContext.resolvedLocaleCode,
          timezone: bootstrap.context.occasion.timezone,
        });

        startTransition(() => {
          setState({
            status: 'ready',
            experience,
            occasionSlug,
            guestToken: activeToken,
            viewerMode: 'guest',
            isReadOnly: false,
            entryMode,
            entryIntent: entryRedirect.intent,
            entryRedirect:
              entryMode === 'invitation_rsvp'
                ? {
                    ...entryRedirect,
                    target: 'rsvp',
                    path: '/rsvp',
                    didFallback: false,
                  }
                : entryRedirect,
            resumePath,
            errorMessage: null,
          });
        });
      } catch (error) {
        if (disposed) {
          return;
        }

        if (shouldRenderGuestAccessOffline(error)) {
          startTransition(() => {
            setState({
              status: 'offline',
              experience: null,
              occasionSlug,
              guestToken: activeToken,
              errorMessage: getGuestAccessOfflineMessage(error),
            });
          });
          return;
        }

        const failure = classifyGuestAccessFailure(error);

        startTransition(() => {
          setState({
            status:
              failure.kind === 'invalid_token'
                ? 'invalid_token'
                : failure.kind === 'occasion_unavailable'
                  ? 'occasion_unavailable'
                  : 'error',
            experience: null,
            occasionSlug,
            guestToken: activeToken,
            errorMessage: failure.message,
          });
        });
      }
    }

    void resolveAccessFromQueryCache();

    return () => {
      disposed = true;
    };
  }, [queryClient, reloadToken]);

  return (
    <GuestAccessContext.Provider
      value={{
        ...state,
        reviewPass: resolveReviewPass(
          typeof window === 'undefined' ? '' : window.location.search,
        ),
        refresh() {
          void invalidateTovQuery(
            queryClient,
            state.occasionSlug && state.guestToken
              ? guestQueryKeys.bootstrap(state.occasionSlug, state.guestToken)
              : guestQueryKeys.access,
          );
          setReloadToken((value) => value + 1);
        },
        clearStoredToken() {
          persistGuestToken(null);
          window.sessionStorage.setItem(guestReadOnlyStorageKey, '0');
          void createOfflineDataCache().clearScope({ app: 'guests' });
          void invalidateTovQuery(
            queryClient,
            state.occasionSlug && state.guestToken
              ? guestQueryKeys.bootstrap(state.occasionSlug, state.guestToken)
              : guestQueryKeys.access,
          );
          setReloadToken((value) => value + 1);
        },
        consumeEntryRedirect() {
          setState((current) =>
            current.status !== 'ready'
              ? current
              : {
                  ...current,
                  entryRedirect: {
                    ...current.entryRedirect,
                    target:
                      current.entryMode === 'invitation_rsvp' ? 'rsvp' : 'home',
                    path:
                      current.entryMode === 'invitation_rsvp' ? '/rsvp' : '/',
                    didFallback: false,
                    reason: 'recommended',
                  },
                },
          );
        },
      }}
    >
      {children}
    </GuestAccessContext.Provider>
  );
}

export function useGuestAccess() {
  const context = useContext(GuestAccessContext);

  if (!context) {
    throw new Error('useGuestAccess must be used within GuestAccessProvider.');
  }

  return context;
}
